import { getAccountsQueryConfig } from '@api/lendingAPI/queryConfigs';
import {
    getTopUpStatus,
    isPreApproved,
    isTopUpApproved
} from '@helpers/GetTopUpStatus/getTopUpStatus';
import {
    fetchGetApplication,
    GetAccountsResponse,
    GetApplicationResponse
} from '@iwoca/lapi-client/edge';
import { useQuery } from '@tanstack/react-query';

import { TTopUpStatus } from '../../../../dataHelpers/GetTopUpStatus/getTopUpStatus.types';
import { useStateKey } from '../../../../iwQueryClient/useStateKey';
import { TProductType } from '../../../dashboard/Dashboard.types';

type TAccount = GetAccountsResponse['data']['accounts'][0];
type TApplication = GetApplicationResponse['data'];
type TDashboardStatus =
    | 'overdue'
    | 'pending'
    | 'topup'
    | 'ongoing'
    | 'ended'
    | 'duetoday'
    | 'applyFlexiLoan'
    | 'pendingFlexiLoan'
    | 'deferred'
    | 'declined'
    | 'unknown';

type TDashboardData = {
    status: TDashboardStatus;
    productType: TProductType;
    topUpStatus: TTopUpStatus;
};

const getProductType = (account: TAccount) => {
    const productType = account.product_type;

    if (productType !== 'flexi24') return productType;

    /* This override is a workaround required due to the removal of the flexi24 product type.
     * If a repeat customer has an account.product_type of flexi24 and receives a new offer, the offer will have a product_type of credit_facility.
     * Legacy dashboard code looks for offers where the product type is the same as the account, which in this case there would be no offers found.
     * The result is that the frontend is in the incorrect state and the customer cannot access their approval.
     * https://iwoca.atlassian.net/browse/TRT-1888 */
    if (isTopUpApproved(account) || isPreApproved(account)) {
        // Repeat offers generated before 9th Jan 2026 still have a product type of flexi24
        // https://iwoca.slack.com/archives/C09A24ARRT8/p1768474277645769
        if (account.latest_formal_offer.product_type === 'flexi24')
            return 'flexi24';

        return 'credit_facility';
    }

    return 'flexi24';
};

export const useGetTabularFlexiDashboardData = (
    accountId: NonNullable<TAccount['account_id']>
): Partial<TDashboardData> => {
    const stateKey = useStateKey();
    const { data: accountsResponse } = useQuery({
        ...getAccountsQueryConfig(stateKey),
        suspense: true
    });

    const { data: applicationResponse } = useQuery({
        queryKey: ['application', stateKey],
        queryFn: () =>
            fetchGetApplication({
                stateKey
            }),
        suspense: true
    });

    const account = accountsResponse?.data.accounts.find(
        (account) => account.account_id === accountId
    );

    if (account && applicationResponse) {
        return {
            status: getDashboardStatus({
                account,
                application: applicationResponse.data
            }),
            productType: getProductType(account),
            topUpStatus: getTopUpStatus(account, applicationResponse.data)
        };
    } else
        return {
            status: undefined,
            productType: undefined,
            topUpStatus: undefined
        };
};

const getDashboardStatus = ({
    account,
    application
}: {
    account: TAccount;
    application: TApplication;
}): TDashboardStatus => {
    const hasOutstandingPrincipal = account.pending_balance!.principal! > 0.1;

    const loanStatus = account.loan_status!;

    const hasFlexiApplication =
        application &&
        application.requested_products!.length > 0 &&
        application.requested_products?.find((requestedProduct) =>
            ['credit_facility', 'flexi24'].includes(
                requestedProduct.product_type!
            )
        );
    const hasOpenApplication = application?.status === 'open';

    if (['overdue', 'pending', 'topup'].includes(loanStatus)) return loanStatus;

    if (loanStatus !== 'ended' && hasOutstandingPrincipal) return 'ongoing';

    if (loanStatus === 'ended') {
        if (hasFlexiApplication) {
            if (hasOpenApplication) return 'pendingFlexiLoan';
            if (['deferred', 'declined'].includes(application.status!))
                return application.status! as TDashboardStatus;
        }
        return 'ended';
    }

    return 'unknown';
};
